// The Feed (/feed, wave 13): Following, Friends and the games you play, with friends playing now and Join. Clips
// from the people and games you follow keep their own page (/clips, community). A single post: /p/:id.

import { api } from "../api.ts";
import { clear, errorBox, h, link } from "../dom.ts";
import { navigate, setTitle } from "../router.ts";
import { signedIn } from "../session.ts";
import { composer, peopleStyles, playingStrip, postCard, type FeedPage, type FeedTab, type Post } from "../people.ts";

const TABS: [FeedTab | "latest", string, string][] = [
  ["following", "Following", "Bangers from the people and studios you follow, and yours."],
  ["friends", "Friends", "Bangers from your friends."],
  ["games", "Your games", "Bangers about the games in your library, and the ones you love or follow."],
  ["latest", "Latest", "What everyone on Banger is posting, the most-hearted lately first."],
];

/** The ids of the games in your library (accounts), for the Games tab. */
async function libraryIds(): Promise<string[]> {
  try {
    const r = await api<{ library?: string[]; hearts?: string[]; follows?: { games?: string[] } }>("accounts", "/me/library");
    const ids = [...(r.library ?? []), ...(r.hearts ?? []), ...(r.follows?.games ?? [])];
    return [...new Set(ids)].filter((id) => /^g_[a-z0-9-]+$/.test(id)).slice(0, 200);
  } catch {
    return [];
  }
}

function signedOut(): HTMLElement {
  return h(
    "div",
    { class: "people-pitch" },
    h("p", { class: "eyebrow" }, "Feed"),
    h("h1", null, "See what your people are playing and making."),
    h("p", { class: "lead" }, "Follow developers and players. Their bangers, releases and the games they share show up here, and every game in a banger is one click from playing."),
    h("div", { class: "actions" }, link(`/signin?next=${encodeURIComponent("/feed")}`, { class: "btn" }, "Sign in"), link("/friends", { class: "btn ghost" }, "What friends get you")),
  );
}

export function peopleFeedPage(_p: Record<string, string>, q: URLSearchParams): HTMLElement {
  peopleStyles();
  setTitle("Feed");
  if (!signedIn()) return h("div", { class: "people" }, signedOut(), latestList(false));
  const query = (q.get("q") ?? "").trim();
  if (query) return searchView(query);
  const want = TABS.find(([t]) => t === q.get("tab"))?.[0] ?? "following";
  if (want === "latest") return latestPage();
  const tab = want as FeedTab;
  const list = h("div", { class: "posts" });
  const more = h("div", { class: "feed-more" });
  const playing = h("div", { class: "feed-playing" });
  let games: string[] | null = null;
  let shown = 0;
  const load = async (before: number | null) => {
    clear(more, h("p", { class: "dim" }, "Loading…"));
    let page: FeedPage;
    try {
      if (tab === "games" && games === null) games = await libraryIds();
      const qs = new URLSearchParams({ tab, limit: "20" });
      if (before) qs.set("before", String(before));
      if (tab === "games" && games?.length) qs.set("games", games.join(","));
      page = await api<FeedPage>("social", `/feed?${qs}`);
    } catch (e) {
      clear(more, errorBox(e));
      return;
    }
    if (before === null) clear(playing, playingStrip(page.playing));
    for (const p of page.posts) list.append(postCard(p, { permalink: true, onDelete: () => list.querySelector(`[data-post="${p.id}"]`)?.remove() }));
    shown += page.posts.length;
    if (page.next) {
      const next = page.next;
      clear(more, h("button", { type: "button", class: "btn ghost", onclick: () => void load(next) }, "Show more"));
    } else if (!shown) {
      clear(more, emptyState(tab, games));
    } else clear(more, h("p", { class: "dim small" }, "You're all caught up."));
  };
  void load(null);
  const onPosted = (p: Post) => {
    list.prepend(postCard(p, { permalink: true, onDelete: () => list.querySelector(`[data-post="${p.id}"]`)?.remove() }));
    if (!shown++) clear(more);
  };
  return h(
    "div",
    { class: "people feed-page" },
    h(
      "header",
      { class: "people-head" },
      h("h1", null, "Feed"),
      h(
        "nav",
        { class: "people-tabs", "aria-label": "Feed" },
        TABS.map(([t, label]) => link(t === "following" ? "/feed" : `/feed?tab=${t}`, { class: "tab", "aria-current": t === tab ? "page" : null }, label)),
        link("/clips", { class: "tab" }, "Clips"),
      ),
      h("p", { class: "dim" }, TABS.find(([t]) => t === tab)![2]),
      searchBox(""),
    ),
    h("div", { class: "feed-cols" }, h("div", { class: "feed-main" }, composer({ onPosted }), list, more), h("aside", { class: "feed-side" }, playing, sideLinks())),
  );
}

function emptyState(tab: FeedTab, games: string[] | null): HTMLElement {
  const [title, line, href, cta] =
    tab === "friends"
      ? ["No bangers from friends yet", "Add friends, or bring your people over from X, and their bangers show up here.", "/friends", "Find friends"]
      : tab === "games"
        ? games?.length
          ? ["Quiet for your games", "Nobody has posted about the games in your library yet. Be the first.", "/browse", "Browse games"]
          : ["No games in your library yet", "Play or add games, and bangers about them show up here.", "/browse", "Browse games"]
        : ["Follow people to fill this up", "Follow developers and players from their profiles and game pages. Their bangers show up here.", "/friends", "Find people"];
  return h("div", { class: "panel" }, h("h2", null, title), h("p", null, line), link(href, { class: "btn" }, cta));
}

function sideLinks(): HTMLElement {
  return h(
    "nav",
    { class: "side-links", "aria-label": "People" },
    link("/friends", null, "Friends"),
    link("/friends/x", null, "Bring your people from X"),
    link("/me/profile", null, "Edit your profile"),
  );
}

/** /p/:id: one post and its replies. Signed out sees adults' posts only (social decides). */
export async function postPage(params: Record<string, string>): Promise<HTMLElement> {
  peopleStyles();
  setTitle("Post");
  const id = params.id ?? "";
  const wrap = h("div", { class: "people post-page" });
  let r: { post: Post; replies: Post[] };
  try {
    r = await api<{ post: Post; replies: Post[] }>("social", signedIn() ? `/posts/${encodeURIComponent(id)}` : `/public/posts/${encodeURIComponent(id)}`, { auth: signedIn() });
  } catch {
    clear(wrap, h("div", { class: "empty" }, h("h1", null, "Post not available"), h("p", { class: "dim" }, "It was deleted, or it isn't shared with you."), link("/feed", { class: "btn" }, "Your feed")));
    return wrap;
  }
  const replies = h("div", { class: "replies" }, r.replies.map((p) => postCard(p, { permalink: false, onDelete: () => replies.querySelector(`[data-post="${p.id}"]`)?.remove() })));
  clear(
    wrap,
    link("/feed", { class: "back" }, "← Feed"),
    postCard(r.post, { permalink: false, onDelete: () => history.back() }),
    r.post.can_reply ? composer({ replyTo: r.post.id, placeholder: "Write a reply", onPosted: (p) => replies.append(postCard(p, { permalink: false })) }) : null,
    !signedIn() ? h("p", { class: "dim small" }, link(`/signin?next=${encodeURIComponent(location.pathname)}`, null, "Sign in"), " to reply.") : null,
    replies,
  );
  return wrap;
}

/** Search bangers (BANGERS.md slice 5): every word must appear; adults' public bangers. */
function searchBox(value: string): HTMLElement {
  const input = h("input", { type: "search", name: "q", value, minlength: 2, maxlength: 60, placeholder: "Search bangers", "aria-label": "Search bangers" }) as HTMLInputElement;
  return h(
    "form",
    {
      class: "feed-search",
      role: "search",
      onsubmit: (e: Event) => {
        e.preventDefault();
        const t = input.value.trim();
        if (t.length >= 2) navigate(`/feed?q=${encodeURIComponent(t)}`);
      },
    },
    input,
  );
}

function searchView(query: string): HTMLElement {
  setTitle(`Search: ${query}`);
  const list = h("div", { class: "posts" });
  const more = h("div", { class: "feed-more" });
  let shown = 0;
  const load = async (before: number | null) => {
    clear(more, h("p", { class: "dim" }, "Searching…"));
    try {
      const qs = new URLSearchParams({ q: query, limit: "20" });
      if (before) qs.set("before", String(before));
      const page = await api<{ posts: Post[]; next: number | null }>("social", `/search/posts?${qs}`);
      for (const p of page.posts) list.append(postCard(p, { permalink: true, onDelete: () => list.querySelector(`[data-post="${p.id}"]`)?.remove() }));
      shown += page.posts.length;
      if (page.next) {
        const next = page.next;
        clear(more, h("button", { type: "button", class: "btn ghost", onclick: () => void load(next) }, "Show more"));
      } else clear(more, h("p", { class: "dim small" }, shown ? "That's everything." : "No bangers match that yet."));
    } catch (e) {
      clear(more, errorBox(e));
    }
  };
  void load(null);
  return h(
    "div",
    { class: "people feed-page" },
    h("header", { class: "people-head" }, h("h1", null, "Search"), link("/feed", { class: "dim" }, "\u2190 Back to your feed"), searchBox(query)),
    h("div", { class: "feed-cols" }, h("div", { class: "feed-main" }, list, more)),
  );
}

/** Latest (BANGERS.md slice 3): everyone's public bangers, ranked by recent hearts. Signed out it sits under the pitch. */
function latestList(authed: boolean): HTMLElement {
  const list = h("div", { class: "posts" });
  const more = h("div", { class: "feed-more" });
  const load = async (offset: number) => {
    clear(more, h("p", { class: "dim" }, "Loading…"));
    try {
      const page = await api<{ posts: Post[]; next: number | null }>("social", `${authed ? "" : "/public"}/timeline?limit=20&offset=${offset}`, { auth: authed });
      for (const p of page.posts) list.append(postCard(p, { permalink: true, onDelete: () => list.querySelector(`[data-post="${p.id}"]`)?.remove() }));
      if (page.next !== null) {
        const next = page.next;
        clear(more, h("button", { type: "button", class: "btn ghost", onclick: () => void load(next) }, "Show more"));
      } else clear(more, h("p", { class: "dim small" }, list.childElementCount ? "That's the last few days." : "Nothing here yet. Be the first to post a banger."));
    } catch (e) {
      clear(more, errorBox(e));
    }
  };
  void load(0);
  return h("section", { class: "feed-latest" }, authed ? null : h("h2", null, "Latest on Banger"), list, more);
}

function latestPage(): HTMLElement {
  const onPosted = (p: Post) => list.prepend(postCard(p, { permalink: true }));
  const body = latestList(true);
  const list = body.querySelector(".posts") as HTMLElement;
  return h(
    "div",
    { class: "people feed-page" },
    h(
      "header",
      { class: "people-head" },
      h("h1", null, "Feed"),
      h("nav", { class: "people-tabs", "aria-label": "Feed" }, TABS.map(([t, label]) => link(t === "following" ? "/feed" : `/feed?tab=${t}`, { class: "tab", "aria-current": t === "latest" ? "page" : null }, label)), link("/clips", { class: "tab" }, "Clips")),
      h("p", { class: "dim" }, TABS.find(([t]) => t === "latest")![2]),
      searchBox(""),
    ),
    h("div", { class: "feed-cols" }, h("div", { class: "feed-main" }, composer({ onPosted }), body)),
  );
}
